import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Menu as MuiMenu,
  MenuItem,
  Link,
  Checkbox,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import Brightness4OutlinedIcon from '@mui/icons-material/Brightness4Outlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { ThemeContext } from '../context/ThemeContext';
import { AuthContext } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import NotificationContainer from '../components/NotificationContainer';
import { getErrorMessage } from '../utils/errorMessage';
import api from '../utils/apiClient';
import { AUTH_API } from '../utils/apiConfig';

// WAYMORR brand tokens (scoped to the login screen)
const BRAND = '#2511A8';
const BRAND_HOVER = '#1d0d88';
const SUISSE = "'Suisse Intl', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const STEM = "'Stem', 'Suisse Intl', sans-serif";
const VIOLET_GRADIENT = 'radial-gradient(120% 120% at 15% 0%, #3a1fd0 0%, #2511A8 42%, #180a6e 100%)';
const DARK_GRADIENT = 'radial-gradient(120% 120% at 15% 0%, #23222c 0%, #131217 55%, #0b0a0e 100%)';

// Юридические документы (лендинг)
const LEGAL = {
  oferta: 'https://monitoring-waymorr.ru/oferta/',
  policy: 'https://monitoring-waymorr.ru/privacy-policy/',
  pd: 'https://monitoring-waymorr.ru/soglasie/',
  ads: 'https://monitoring-waymorr.ru/rassylka/',
};

const LANGS = [
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [openRegister, setOpenRegister] = useState(false);
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    telegram: '',
    info: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [consentPd, setConsentPd] = useState(false);
  const [consentOffer, setConsentOffer] = useState(false);
  const [consentAds, setConsentAds] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [openForgot, setOpenForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailError, setForgotEmailError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const navigate = useNavigate();
  const { toggleTheme } = useContext(ThemeContext);
  const { login, register } = useContext(AuthContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const ACCENT = isDark ? '#9d8bff' : BRAND; // brighter violet for links on dark bg
  const { showError, showSuccess } = useNotifications();
  const { t, i18n } = useTranslation('auth');
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const currentLang = LANGS.find((l) => i18n.language && i18n.language.startsWith(l.code)) || LANGS[0];

  const handleLogin = async () => {
    if (!email || !password) {
      showError(t('fillAllFields'));
      return;
    }

    try {
      setLoginLoading(true);
      await login(email, password);
      showSuccess(t('loginSuccess'));
      navigate('/dashboard');
    } catch (error) {
      showError(t('loginError', { message: getErrorMessage(error) }));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!registerData.name.trim()) {
      newErrors.name = t('nameRequired');
    }

    if (!registerData.email.trim()) {
      newErrors.email = t('emailRequiredError');
    } else if (!validateEmail(registerData.email)) {
      newErrors.email = t('emailInvalid');
    }

    if (!registerData.password) {
      newErrors.password = t('passwordRequiredError');
    } else if (registerData.password.length < 6) {
      newErrors.password = t('passwordMinLength');
    }

    if (!consentPd) {
      newErrors.consentPd = t('consentRequired', 'Необходимо согласие');
    }
    if (!consentOffer) {
      newErrors.consentOffer = t('consentRequired', 'Необходимо согласие');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setRegisterLoading(true);

      await register({
        email: registerData.email,
        password: registerData.password,
        name: registerData.name,
        telegram: registerData.telegram || undefined,
        info: registerData.info || undefined
      });

      showSuccess(t('registerSuccess'));
      setOpenRegister(false);
      setRegisterData({
        name: '',
        email: '',
        telegram: '',
        info: '',
        password: ''
      });
      setConsentPd(false);
      setConsentOffer(false);
      setConsentAds(false);
      setErrors({});
    } catch (error) {
      showError(t('registerError', { message: getErrorMessage(error) }));
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setRegisterData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleForgotPassword = async () => {
    const trimmed = forgotEmail.trim();
    if (!trimmed) {
      setForgotEmailError(t('emailRequiredError'));
      return;
    }
    if (!validateEmail(trimmed)) {
      setForgotEmailError(t('emailInvalid'));
      return;
    }

    try {
      setForgotLoading(true);
      setForgotEmailError('');
      await api.post(`${AUTH_API}/v1/auth/password/recovery/request`, { email: trimmed });
      showSuccess(t('forgotPasswordSent'));
      setOpenForgot(false);
      setForgotEmail('');
    } catch (error) {
      showError(t('forgotPasswordError', { message: getErrorMessage(error) }));
    } finally {
      setForgotLoading(false);
    }
  };

  // Shared outlined-input styling (rounded, brand focus)
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '11px',
      fontFamily: SUISSE,
      '&.Mui-focused fieldset': { borderColor: BRAND, borderWidth: '1.5px' },
    },
    '& label.Mui-focused': { color: BRAND },
  };

  const stats = [
    { v: '24/7', l: t('statUptime', 'контроль парка') },
    { v: '< 1 ' + t('statMin', 'мин'), l: t('statReaction', 'реакция на сбой') },
    { v: '100%', l: t('statData', 'ваши данные') },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', fontFamily: SUISSE, bgcolor: 'background.paper' }}>
      {/* ---------- BRAND PANEL (desktop) ---------- */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: { md: '46%', lg: '44%' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          p: { md: 6 },
          color: '#fff',
          background: isDark ? DARK_GRADIENT : VIOLET_GRADIENT,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            WebkitMaskImage: 'radial-gradient(80% 60% at 90% 90%, #000, transparent)',
            maskImage: 'radial-gradient(80% 60% at 90% 90%, #000, transparent)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box component="img" src="/monitoring-logo-white.png" alt="WAYMORR MONITORING" sx={{ height: { md: 40, lg: 46 } }} />
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 440 }}>
          <Typography sx={{ fontFamily: STEM, fontWeight: 700, fontSize: { md: 34, lg: 40 }, lineHeight: 1.12, letterSpacing: '-0.01em' }}>
            {t('brandHeadline', 'Профессиональный мониторинг ASIC‑майнеров')}
          </Typography>
          <Typography sx={{ mt: 2.5, fontSize: 15.5, lineHeight: 1.6, color: 'rgba(255,255,255,.8)', maxWidth: 400 }}>
            {t('brandSubtitle', 'Хешрейт, температуры, доходность и алерты по всему парку оборудования — в реальном времени, из единой панели.')}
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', gap: 4 }}>
          {stats.map((s, i) => (
            <Box key={i}>
              <Typography sx={{ fontFamily: STEM, fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}>{s.v}</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'rgba(255,255,255,.65)', mt: 0.5 }}>{s.l}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ---------- FORM SIDE ---------- */}
      <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, sm: 4 } }}>
        {/* top-right controls: language (flag + name) + theme */}
        <Box sx={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            onClick={(e) => setLangAnchorEl(e.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              textTransform: 'none',
              fontFamily: SUISSE,
              fontWeight: 500,
              fontSize: 14,
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '9px',
              px: 1.5,
              py: 0.75,
              '& .MuiButton-endIcon': { ml: 0.25 },
            }}
          >
            <Box component="span" sx={{ fontSize: 17, mr: 0.75, lineHeight: 1 }}>{currentLang.flag}</Box>
            {currentLang.label}
          </Button>
          <MuiMenu anchorEl={langAnchorEl} open={Boolean(langAnchorEl)} onClose={() => setLangAnchorEl(null)}>
            {LANGS.map((lang) => (
              <MenuItem
                key={lang.code}
                selected={currentLang.code === lang.code}
                onClick={() => { i18n.changeLanguage(lang.code); setLangAnchorEl(null); }}
                sx={{ fontFamily: SUISSE, fontSize: 14, gap: 1 }}
              >
                <Box component="span" sx={{ fontSize: 17, lineHeight: 1 }}>{lang.flag}</Box>
                {lang.label}
              </MenuItem>
            ))}
          </MuiMenu>
          <IconButton onClick={toggleTheme} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '9px', width: 40, height: 40 }} title={t('themeToggle', 'Тема')}>
            <Brightness4OutlinedIcon />
          </IconButton>
        </Box>

        {/* form */}
        <Box sx={{ width: '100%', maxWidth: 392 }}>
          {/* logo for mobile (brand panel hidden) */}
          <Box
            component="img"
            src={isDark ? '/monitoring-logo-white.png' : '/monitoring-logo-violet.png'}
            alt="WAYMORR MONITORING"
            sx={{ height: 34, mb: 4, display: { xs: 'block', md: 'none' } }}
          />

          <Typography component="h1" sx={{ fontFamily: STEM, fontWeight: 700, fontSize: 30, letterSpacing: '-0.01em', color: 'text.primary' }}>
            {t('loginTitle')}
          </Typography>
          <Typography sx={{ mt: 1.25, fontSize: 15, color: 'text.secondary', lineHeight: 1.5 }}>
            {t('loginSubtitle')}
          </Typography>

          <TextField
            label={t('emailLabel')}
            variant="outlined"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={fieldSx}
          />
          <TextField
            label={t('passwordLabel')}
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={fieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" tabIndex={-1}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            onClick={handleLogin}
            disabled={loginLoading}
            sx={{
              mt: 3,
              py: 1.5,
              borderRadius: '11px',
              fontFamily: SUISSE,
              fontSize: 16,
              fontWeight: 600,
              textTransform: 'none',
              color: '#fff',
              bgcolor: BRAND,
              boxShadow: '0 12px 26px -12px rgba(37,17,168,.7)',
              '&:hover': { bgcolor: BRAND_HOVER },
              '&.Mui-disabled': { bgcolor: BRAND, opacity: 0.7, color: '#fff' },
            }}
          >
            {loginLoading ? <CircularProgress size={24} color="inherit" /> : t('loginButton')}
          </Button>

          <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, fontSize: 14 }}>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              {t('noAccount', 'Нет аккаунта?')}{' '}
              <Link component="button" type="button" onClick={() => setOpenRegister(true)} sx={{ fontFamily: SUISSE, fontWeight: 600, color: ACCENT, textDecoration: 'none' }}>
                {t('registerButton')}
              </Link>
            </Typography>
            <Link component="button" type="button" onClick={() => setOpenForgot(true)} sx={{ fontFamily: SUISSE, fontWeight: 600, color: ACCENT, textDecoration: 'none', fontSize: 14 }}>
              {t('forgotPassword')}
            </Link>
          </Box>
        </Box>

        <Box sx={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center', px: 2 }}>
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
            © 2026 WAYMORR · ООО «ВЭЙМОРР АЙТИ» · ИНН 9722109490
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
            {t('certLine', 'Программа для ЭВМ «WAYMORR Monitoring» · свидетельство № 2026612836 от 30.01.2026')}
          </Typography>
        </Box>
      </Box>

      {/* ---------- Register dialog ---------- */}
      <Dialog open={openRegister} onClose={() => setOpenRegister(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: STEM, fontWeight: 700 }}>{t('registerTitle')}</DialogTitle>
        <DialogContent>
          <TextField label={t('nameLabel')} variant="outlined" fullWidth margin="normal" value={registerData.name}
            onChange={(e) => handleInputChange('name', e.target.value)} error={!!errors.name} helperText={errors.name} sx={fieldSx} />
          <TextField label={t('emailRequired')} variant="outlined" fullWidth margin="normal" value={registerData.email}
            onChange={(e) => handleInputChange('email', e.target.value)} error={!!errors.email} helperText={errors.email} sx={fieldSx} />
          <TextField label={t('passwordRequired')} type="password" variant="outlined" fullWidth margin="normal" value={registerData.password}
            onChange={(e) => handleInputChange('password', e.target.value)} error={!!errors.password} helperText={errors.password} sx={fieldSx} />
          <TextField label={t('telegramLabel')} variant="outlined" fullWidth margin="normal" value={registerData.telegram}
            onChange={(e) => handleInputChange('telegram', e.target.value)} placeholder="@username" sx={fieldSx} />
          <TextField label={t('infoLabel')} variant="outlined" fullWidth margin="normal" multiline rows={3} value={registerData.info}
            onChange={(e) => handleInputChange('info', e.target.value)} sx={fieldSx} />

          <Box sx={{ mt: 1.5 }}>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0 }}
              control={
                <Checkbox
                  checked={consentPd}
                  onChange={(e) => { setConsentPd(e.target.checked); if (errors.consentPd) setErrors((p) => ({ ...p, consentPd: '' })); }}
                  sx={{ py: 0.25, color: errors.consentPd ? 'error.main' : BRAND, '&.Mui-checked': { color: BRAND } }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: '7px' }}>
                  {t('consentPdText', 'Я даю ')}
                  <Link href={LEGAL.pd} target="_blank" rel="noopener noreferrer" sx={{ color: ACCENT, fontWeight: 500 }}>
                    {t('consentPdLink', 'согласие на обработку персональных данных')}
                  </Link>
                </Typography>
              }
            />
            {errors.consentPd && <FormHelperText error sx={{ ml: '34px', mt: 0 }}>{errors.consentPd}</FormHelperText>}

            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0, mt: 0.5 }}
              control={
                <Checkbox
                  checked={consentOffer}
                  onChange={(e) => { setConsentOffer(e.target.checked); if (errors.consentOffer) setErrors((p) => ({ ...p, consentOffer: '' })); }}
                  sx={{ py: 0.25, color: errors.consentOffer ? 'error.main' : BRAND, '&.Mui-checked': { color: BRAND } }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: '7px' }}>
                  {t('consentOfferText', 'Я принимаю условия ')}
                  <Link href={LEGAL.oferta} target="_blank" rel="noopener noreferrer" sx={{ color: ACCENT, fontWeight: 500 }}>
                    {t('consentOfferLink', 'Оферты на использование программы')}
                  </Link>
                  {t('consentOfferAnd', ' и ')}
                  <Link href={LEGAL.policy} target="_blank" rel="noopener noreferrer" sx={{ color: ACCENT, fontWeight: 500 }}>
                    {t('consentPolicyLink', 'Политики конфиденциальности')}
                  </Link>
                </Typography>
              }
            />
            {errors.consentOffer && <FormHelperText error sx={{ ml: '34px', mt: 0 }}>{errors.consentOffer}</FormHelperText>}

            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0, mt: 0.5 }}
              control={
                <Checkbox
                  checked={consentAds}
                  onChange={(e) => setConsentAds(e.target.checked)}
                  sx={{ py: 0.25, color: BRAND, '&.Mui-checked': { color: BRAND } }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: '7px' }}>
                  {t('consentAdsText', 'Согласен получать ')}
                  <Link href={LEGAL.ads} target="_blank" rel="noopener noreferrer" sx={{ color: ACCENT, fontWeight: 500 }}>
                    {t('consentAdsLink', 'рекламные и информационные рассылки')}
                  </Link>
                  {t('consentAdsTail', ' (необязательно)')}
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenRegister(false)} disabled={registerLoading} sx={{ textTransform: 'none', fontFamily: SUISSE }}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleRegister} variant="contained" disabled={registerLoading || !consentPd || !consentOffer}
            sx={{ textTransform: 'none', fontFamily: SUISSE, fontWeight: 600, borderRadius: '9px', bgcolor: BRAND, '&:hover': { bgcolor: BRAND_HOVER } }}>
            {registerLoading ? <CircularProgress size={24} color="inherit" /> : t('registerSubmit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Forgot password dialog ---------- */}
      <Dialog open={openForgot} onClose={() => { if (!forgotLoading) setOpenForgot(false); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: STEM, fontWeight: 700 }}>{t('forgotPasswordTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, mb: 1 }}>
            {t('forgotPasswordHint')}
          </Typography>
          <TextField
            label={t('emailLabel')} variant="outlined" fullWidth margin="normal" value={forgotEmail}
            onChange={(e) => { setForgotEmail(e.target.value); if (forgotEmailError) setForgotEmailError(''); }}
            onKeyPress={(e) => { if (e.key === 'Enter') handleForgotPassword(); }}
            error={!!forgotEmailError} helperText={forgotEmailError} disabled={forgotLoading} sx={fieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenForgot(false)} disabled={forgotLoading} sx={{ textTransform: 'none', fontFamily: SUISSE }}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleForgotPassword} variant="contained" disabled={forgotLoading}
            sx={{ textTransform: 'none', fontFamily: SUISSE, fontWeight: 600, borderRadius: '9px', bgcolor: BRAND, '&:hover': { bgcolor: BRAND_HOVER } }}>
            {forgotLoading ? <CircularProgress size={24} color="inherit" /> : t('forgotPasswordSubmit')}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationContainer />
    </Box>
  );
};

export default Login;
